import { useEffect } from "react";
import { useViewSettings } from "@/view-settings";
import { cameraIds$, camerasMesh$, hiddenTracks$ } from "../state";
import { ListenerFn } from "@legendapp/state";
import { selectedTrack$, visibleTracksSnapshots$ } from "../state";
import { Snapshot, TrackID } from "@/shared/types";
import { Color } from "three";
import { parseLastDetectionsMappingJSON } from "@/contexts/playback-context/helpers";
import { animalAvatarGenerator } from "@/shared/lib/animal-avatar";

const highlightedCameras = new Set();
const trackCameras = new Map<string, string>();

const dummyColor = new Color("white");

export const highlightCamera = (
  cameraId: string,
  trackId?: string,
  isDefault?: boolean
) => {
  const camera = camerasMesh$.peek();

  if (!camera) return;

  const color = trackId
    ? animalAvatarGenerator.getRandomAvatar(trackId)
    : { colorHex: "rgb(58, 23, 211)" };

  const instanceId = cameraIds$.peek().findIndex((id) => id === cameraId);

  if (instanceId > -1) {
    camera.setColorAt(
      instanceId,
      dummyColor.set(
        isDefault ? "white" : color?.colorHex || "rgb(58, 23, 211)"
      )
    );
    camera.instanceColor!.needsUpdate = true;
  }
};

export const highlightLastDetections = (
  visibleTracks: Map<string, any>,
  hiddenTracks: Set<string>
) => {
  const snapshots = [...visibleTracks.values()] as Snapshot[];
  const trackIds = [...visibleTracks.keys()] as string[];

  highlightedCameras.clear();
  trackCameras.clear();

  snapshots.forEach((snapshot, index) => {
    const currentTrackId = trackIds[index];

    if (hiddenTracks.has(currentTrackId)) {
      return;
    }

    const lastDetectionsMapping = parseLastDetectionsMappingJSON(
      snapshot.last_detections_mapping
    );

    lastDetectionsMapping?.forEach((detection: any) => {
      highlightedCameras.add(detection.sensor_id);

      const selectedTrack = selectedTrack$.peek();

      if (selectedTrack && selectedTrack === currentTrackId) {
        trackCameras.set(detection.sensor_id, selectedTrack);
      }
    });
  });

  const cameras = cameraIds$.peek();

  cameras.forEach((cameraId) => {
    if (highlightedCameras.has(cameraId)) {
      const trackId = trackCameras.get(cameraId);
      highlightCamera(cameraId, trackId);
    } else {
      highlightCamera(cameraId, undefined, true);
    }
  });
};

const onChange: ListenerFn<Map<string, any>> = (params) => {
  highlightLastDetections(params.value, hiddenTracks$.peek());
};

const onHiddenTracksChange: ListenerFn<Set<string>> = (params) => {
  highlightLastDetections(visibleTracksSnapshots$.peek(), params.value);
};

visibleTracksSnapshots$.onChange(onChange);
hiddenTracks$.onChange(onHiddenTracksChange);

export const useHighlightCamera = (
  snapshot: Snapshot | null,
  trackId: TrackID
) => {
  const viewSettings = useViewSettings();
  const shouldHighlightCamera = viewSettings.showDetectionCamera.get();

  useEffect(() => {
    if (snapshot && shouldHighlightCamera && !hiddenTracks$.has(trackId)) {
      visibleTracksSnapshots$.set(trackId, snapshot);
    } else {
      visibleTracksSnapshots$.delete(trackId);
    }
  }, [snapshot, trackId, shouldHighlightCamera]);
};
